import React, { useState, useEffect } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    TextField,
    CircularProgress,
    Alert,
    Chip,
    Divider,
    Stepper,
    Step,
    StepLabel,
    StepContent,
    Paper,
    IconButton,
    Tooltip,
    Grid,
} from "@mui/material";
import {
    PlayArrow as PlayArrowIcon,
    Close as CloseIcon,
    CheckCircle as CheckCircleIcon,
    Error as ErrorIcon,
    Send as SendIcon,
    ContentCopy as CopyIcon,
    Refresh as RefreshIcon,
} from "@mui/icons-material";
import Api from "../../services/api";
import { PapiroConsole } from "../../utils/papiroConsole";

const styles = {
    dialogTitle: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        borderBottom: "1px solid #e0e0e0",
    },
    codeBlock: {
        backgroundColor: "#1e1e1e",
        color: "#d4d4d4",
        fontFamily: "monospace",
        fontSize: "12px",
        padding: "12px",
        borderRadius: "4px",
        overflow: "auto",
        maxHeight: "180px",
        whiteSpace: "pre-wrap",
        wordBreak: "break-all",
    },
    responseHeader: {
        display: "flex",
        alignItems: "center",
        gap: "8px",
        marginBottom: "8px",
    },
    statusChip: {
        fontWeight: 600,
    },
    stepContent: {
        paddingTop: "8px",
        paddingBottom: "16px",
    },
    infoBox: {
        backgroundColor: "#f5f5f5",
        padding: "12px",
        borderRadius: "4px",
        marginBottom: "12px",
    },
    copyButton: {
        position: "absolute",
        top: "4px",
        right: "4px",
    },
    codeContainer: {
        position: "relative",
    },
};

export default function WebhookTestModalAutomation({
    open,
    onClose,
    webhookId,
    payloadFormat,
    payloadTemplate,
    webhookName,
    webhookUrl,
}) {
    const [activeStep, setActiveStep] = useState(0);
    const [testing, setTesting] = useState(false);
    const [testResult, setTestResult] = useState(null);
    const [testError, setTestError] = useState(null);
    const [sampleCalledId, setSampleCalledId] = useState("");
    const [resolvedPayload, setResolvedPayload] = useState("");

    // Reset state quando o modal abre
    useEffect(() => {
        if (open) {
            setActiveStep(0);
            setTestResult(null);
            setTestError(null);
            setSampleCalledId("");
            setResolvedPayload("");
        }
    }, [open]);

    // Handler para executar teste
    const handleTest = async () => {
        if (!webhookId) {
            setTestError("Selecione um webhook para testar.");
            return;
        }

        if (!payloadTemplate) {
            setTestError("Defina um template de payload primeiro.");
            return;
        }

        // Validar sintaxe JSON antes de enviar (apenas para formato JSON)
        if (payloadFormat === "CUSTOM_JSON" || !payloadFormat) {
            try {
                JSON.parse(payloadTemplate);
            } catch (e) {
                setTestError("JSON inválido: " + e.message);
                return;
            }
        }

        setTesting(true);
        setTestError(null);
        setTestResult(null);
        setActiveStep(1);

        try {
            const response = await Api.post("/automation/webhook/test", {
                webhookId: webhookId,
                payloadTemplate: payloadTemplate,
                payloadFormat: payloadFormat || "CUSTOM_JSON",
                sampleCalledId: sampleCalledId ? parseInt(sampleCalledId) : null,
            });

            if (response.data?.success) {
                setTestResult(response.data.data);
                setResolvedPayload(response.data.data?.processedpayload || "");
                setActiveStep(2);
            } else {
                setTestError(response.data?.errors?.join(", ") || "Erro ao testar webhook");
                setActiveStep(2);
            }
        } catch (err) {
            PapiroConsole.log("Erro ao testar webhook:", err);
            setTestError(err.response?.data?.errors?.join(", ") || "Erro ao testar webhook. Verifique a conexao.");
            setActiveStep(2);
        } finally {
            setTesting(false);
        }
    };

    // Copiar para clipboard
    const handleCopy = (text) => {
        navigator.clipboard.writeText(text);
    };

    // Obter cor do status HTTP
    const getStatusColor = (statusCode) => {
        if (statusCode >= 200 && statusCode < 300) return "success";
        if (statusCode >= 400 && statusCode < 500) return "warning";
        if (statusCode >= 500) return "error";
        return "default";
    };

    // Formatar tempo de resposta
    const formatResponseTime = (ms) => {
        if (ms < 1000) return `${ms}ms`;
        return `${(ms / 1000).toFixed(2)}s`;
    };

    const steps = [
        {
            label: "Configuracao",
            description: "Configure os parametros do teste",
        },
        {
            label: "Executando",
            description: "Enviando requisicao para o webhook",
        },
        {
            label: "Resultado",
            description: "Analise a resposta do webhook",
        },
    ];

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
        >
            <DialogTitle sx={styles.dialogTitle}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <PlayArrowIcon color="primary" />
                    <Typography variant="h6">Testar Webhook</Typography>
                </Box>
                <IconButton onClick={onClose} size="small">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent dividers>
                <div style={{ maxHeight: "60vh", overflowY: "auto" }}>
                {/* Info do Webhook */}
                <Box sx={styles.infoBox}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                            <Typography variant="caption" color="text.secondary">
                                Webhook
                            </Typography>
                            <Typography variant="body2" fontWeight={500}>
                                {webhookName || "Nao selecionado"}
                            </Typography>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Typography variant="caption" color="text.secondary">
                                URL
                            </Typography>
                            <Typography variant="body2" noWrap title={webhookUrl}>
                                {webhookUrl || "-"}
                            </Typography>
                        </Grid>
                    </Grid>
                </Box>

                {/* Stepper */}
                <Stepper activeStep={activeStep} orientation="vertical">
                    {/* Step 1: Configuracao */}
                    <Step>
                        <StepLabel>{steps[0].label}</StepLabel>
                        <StepContent>
                            <Box sx={styles.stepContent}>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                    Opcionalmente, informe um ID de chamado para usar dados reais no teste.
                                    Se nao informar, serao usados dados de exemplo.
                                </Typography>

                                <TextField
                                    label="ID do Chamado (opcional)"
                                    value={sampleCalledId}
                                    onChange={(e) => setSampleCalledId(e.target.value.replace(/\D/g, ""))}
                                    placeholder="Ex: 12345"
                                    size="small"
                                    sx={{ width: 200 }}
                                    helperText="Deixe vazio para usar dados de exemplo"
                                />

                                <Divider sx={{ my: 2 }} />

                                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                    Template do Payload:
                                </Typography>
                                <Box sx={styles.codeContainer}>
                                    <Box sx={styles.codeBlock}>
                                        {payloadTemplate || "Nenhum template definido"}
                                    </Box>
                                    <Tooltip title="Copiar">
                                        <IconButton
                                            size="small"
                                            onClick={() => handleCopy(payloadTemplate)}
                                            sx={styles.copyButton}
                                        >
                                            <CopyIcon fontSize="small" sx={{ color: "#fff" }} />
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                            </Box>
                        </StepContent>
                    </Step>

                    {/* Step 2: Executando */}
                    <Step>
                        <StepLabel>{steps[1].label}</StepLabel>
                        <StepContent>
                            <Box sx={{ ...styles.stepContent, textAlign: "center", py: 4 }}>
                                <CircularProgress size={40} />
                                <Typography variant="body1" sx={{ mt: 2 }}>
                                    Enviando requisicao...
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Aguarde enquanto o webhook e executado
                                </Typography>
                            </Box>
                        </StepContent>
                    </Step>

                    {/* Step 3: Resultado */}
                    <Step>
                        <StepLabel
                            error={!!testError}
                            optional={
                                testResult && testResult.statuscode != null && (
                                    <Chip
                                        size="small"
                                        label={`${testResult.statuscode}`}
                                        color={getStatusColor(testResult.statuscode)}
                                        sx={styles.statusChip}
                                    />
                                )
                            }
                        >
                            {steps[2].label}
                        </StepLabel>
                        <StepContent>
                            <Box sx={styles.stepContent}>
                                {testError && (
                                    <Alert severity="error" sx={{ mb: 2 }}>
                                        {testError}
                                    </Alert>
                                )}

                                {testResult && (
                                    <>
                                        {/* Status e Tempo */}
                                        <Box sx={styles.responseHeader}>
                                            {testResult.success ? (
                                                <CheckCircleIcon color="success" />
                                            ) : (
                                                <ErrorIcon color="error" />
                                            )}
                                            <Typography variant="body1" fontWeight={500}>
                                                {testResult.success ? "Sucesso" : "Falha"}
                                            </Typography>
                                            {testResult.statuscode != null && (
                                                <Chip
                                                    size="small"
                                                    label={`HTTP ${testResult.statuscode}`}
                                                    color={getStatusColor(testResult.statuscode)}
                                                />
                                            )}
                                            {testResult.responsetimems != null && (
                                                <Chip
                                                    size="small"
                                                    label={formatResponseTime(testResult.responsetimems)}
                                                    variant="outlined"
                                                />
                                            )}
                                        </Box>

                                        {/* Payload Enviado */}
                                        <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
                                            Payload Enviado:
                                        </Typography>
                                        <Box sx={styles.codeContainer}>
                                            <Box sx={styles.codeBlock}>
                                                {resolvedPayload || testResult.processedpayload || "-"}
                                            </Box>
                                            <Tooltip title="Copiar">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleCopy(resolvedPayload || testResult.processedpayload)}
                                                    sx={styles.copyButton}
                                                >
                                                    <CopyIcon fontSize="small" sx={{ color: "#fff" }} />
                                                </IconButton>
                                            </Tooltip>
                                        </Box>

                                        {/* Request Headers */}
                                        {testResult.requestheaders && Object.keys(testResult.requestheaders).length > 0 && (
                                            <>
                                                <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
                                                    Headers Enviados:
                                                </Typography>
                                                <Box sx={styles.codeContainer}>
                                                    <Box sx={styles.codeBlock}>
                                                        {Object.entries(testResult.requestheaders).map(([key, value]) => (
                                                            <div key={key}><strong>{key}:</strong> {value}</div>
                                                        ))}
                                                    </Box>
                                                    <Tooltip title="Copiar">
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleCopy(JSON.stringify(testResult.requestheaders, null, 2))}
                                                            sx={styles.copyButton}
                                                        >
                                                            <CopyIcon fontSize="small" sx={{ color: "#fff" }} />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Box>
                                            </>
                                        )}

                                        {/* Response Headers */}
                                        {testResult.responseheaders && Object.keys(testResult.responseheaders).length > 0 && (
                                            <>
                                                <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
                                                    Headers da Resposta:
                                                </Typography>
                                                <Box sx={styles.codeContainer}>
                                                    <Box sx={styles.codeBlock}>
                                                        {Object.entries(testResult.responseheaders).map(([key, value]) => (
                                                            <div key={key}><strong>{key}:</strong> {value}</div>
                                                        ))}
                                                    </Box>
                                                    <Tooltip title="Copiar">
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleCopy(JSON.stringify(testResult.responseheaders, null, 2))}
                                                            sx={styles.copyButton}
                                                        >
                                                            <CopyIcon fontSize="small" sx={{ color: "#fff" }} />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Box>
                                            </>
                                        )}

                                        {/* Response Body */}
                                        {testResult.responsebody && (
                                            <>
                                                <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
                                                    Resposta do Servidor:
                                                </Typography>
                                                <Box sx={styles.codeContainer}>
                                                    <Box sx={styles.codeBlock}>
                                                        {testResult.responsebody}
                                                    </Box>
                                                    <Tooltip title="Copiar">
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleCopy(testResult.responsebody)}
                                                            sx={styles.copyButton}
                                                        >
                                                            <CopyIcon fontSize="small" sx={{ color: "#fff" }} />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Box>
                                            </>
                                        )}

                                        {/* Error Message */}
                                        {testResult.errormessage && (
                                            <Alert severity="warning" sx={{ mt: 2 }}>
                                                {testResult.errormessage}
                                            </Alert>
                                        )}
                                    </>
                                )}
                            </Box>
                        </StepContent>
                    </Step>
                </Stepper>
                </div>
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2, borderTop: "1px solid #e0e0e0" }}>
                <Button onClick={onClose} color="inherit">
                    Fechar
                </Button>

                {activeStep === 0 && (
                    <Button
                        variant="contained"
                        onClick={handleTest}
                        disabled={testing || !webhookId}
                        startIcon={testing ? <CircularProgress size={16} /> : <SendIcon />}
                    >
                        Executar Teste
                    </Button>
                )}

                {activeStep === 2 && (
                    <Button
                        variant="outlined"
                        onClick={() => {
                            setActiveStep(0);
                            setTestResult(null);
                            setTestError(null);
                        }}
                        startIcon={<RefreshIcon />}
                    >
                        Testar Novamente
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
}
